import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import VersionService, { VersionInfo } from 'src/services/version/version.service';

const VersionUpdateNotification: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);

  useEffect(() => {
    const versionService = VersionService;

    // Get current version on mount
    versionService.getCurrentVersion();

    // Listen for version updates
    const unsubscribe = versionService.onVersionUpdate((version) => {
      setLatestVersion(version);
      setUpdateAvailable(true);
    });

    // Start periodic checking
    versionService.startPeriodicCheck(5 * 60 * 1000); // Check every 5 minutes

    return () => {
      unsubscribe();
      versionService.stopPeriodicCheck();
    };
  }, []);

  const handleRefresh = async() => {
    // Update the current version before refreshing so we don't show the notification again
    if (latestVersion) {
      VersionService.updateCurrentVersion(latestVersion);
    }
    await VersionService.forceUpdate();
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[10000] max-w-md animate-in slide-in-from-bottom-2">
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 flex items-start gap-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            New Version Available
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            A new version of the application is available. Click refresh to update.
          </p>
          {latestVersion && (
            <div className="text-xs text-gray-500 mb-3">
              Build:
              {' '}
              {latestVersion.buildNumber}
              {' '}
              •
              {' '}
              {new Date(latestVersion.buildTime).toLocaleString()}
            </div>
          )}
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh to Update
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default VersionUpdateNotification;
